import { Head, Link, useForm, usePage } from "@inertiajs/react"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { connectedAccountsGithubPath, connectedAccountsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface CurrentConfig {
  github_repo: string
  github_default_branch: string
  github_posts_path: string
  github_images_path: string
}

interface ConfigureProps {
  installation_id: string
  repositories: { full_name: string }[]
  current_config: CurrentConfig | null
  [key: string]: unknown
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Connected Accounts", href: connectedAccountsPath() },
  { title: "GitHub", href: "#" },
]

export default function GitHubConfigure() {
  const { repositories, current_config } = usePage<ConfigureProps>().props
  const { data, setData, post, processing, errors } = useForm({
    github_repo: current_config?.github_repo ?? "",
    github_default_branch: current_config?.github_default_branch ?? "main",
    github_posts_path: current_config?.github_posts_path ?? "_posts",
    github_images_path: current_config?.github_images_path ?? "assets/images",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(connectedAccountsGithubPath())
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Configure GitHub Site" />

      <div className="space-y-6 px-4 py-6">
        <HeadingSmall
          title="Configure GitHub Site"
          description="Enter your GitHub repository details to complete the connection."
        />

        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="github_repo">Repository</Label>
            {repositories.length > 0 ? (
              <Select
                value={data.github_repo}
                onValueChange={(value) => setData("github_repo", value)}
              >
                <SelectTrigger id="github_repo">
                  <SelectValue placeholder="Select a repository" />
                </SelectTrigger>
                <SelectContent>
                  {repositories.map((repo) => (
                    <SelectItem key={repo.full_name} value={repo.full_name}>
                      {repo.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-muted-foreground text-sm">
                No repositories found. Please grant the GitHub App access to at
                least one repository.
              </p>
            )}
            {errors.github_repo && (
              <p className="text-destructive text-sm">{errors.github_repo}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="github_default_branch">Default Branch</Label>
            <Input
              id="github_default_branch"
              value={data.github_default_branch}
              onChange={(e) => setData("github_default_branch", e.target.value)}
              placeholder="main"
            />
            {errors.github_default_branch && (
              <p className="text-destructive text-sm">
                {errors.github_default_branch}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="github_posts_path">Posts Directory</Label>
            <Input
              id="github_posts_path"
              value={data.github_posts_path}
              onChange={(e) => setData("github_posts_path", e.target.value)}
              placeholder="_posts"
            />
            {errors.github_posts_path && (
              <p className="text-destructive text-sm">
                {errors.github_posts_path}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="github_images_path">Images Directory</Label>
            <Input
              id="github_images_path"
              value={data.github_images_path}
              onChange={(e) => setData("github_images_path", e.target.value)}
              placeholder="assets/images"
            />
            {errors.github_images_path && (
              <p className="text-destructive text-sm">
                {errors.github_images_path}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={processing}>
              Save
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link href={connectedAccountsPath()}>Cancel</Link>
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
